import * as React from 'react';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import selectSurfaceActionsOption from '../actions/selectSurfaceActionsOption';
import getStore from '../store/store';
import strings from 'owa-strings/owa-options-strings';
import SurfaceActionsOptionState from '../store/schema/SurfaceActionsOptionState';
import SurfaceActionsShared from 'owa-surface-actions-view/lib/components/SurfaceActionsShared';
import { SurfaceType } from 'owa-surface-actions-view';
import { ActionKey } from 'owa-outlook-service-options';
import { AddinCommandSurfaceItem, ExtensibilityModeEnum } from 'owa-addins-types';
import { getFallbackValueIfNull } from 'owa-options-core';
import { getHoverSurfaceAction } from 'owa-surface-actions-option';
import { isFeatureEnabled } from 'owa-feature-flags';
import { reactive } from 'satcheljs/lib/legacy/react';
import { ReadingPanePosition } from 'owa-reading-pane-option';
import {
    SurfaceActionIcon,
    getHoverActionIconName,
} from 'owa-surface-actions/lib/utils/getIconNameFromKey';
import {
    getOptionsForFeature,
    SurfaceActionsOptions,
    OwsOptionsFeatureType,
} from 'owa-outlook-service-options';
import {
    getAddinOptionSurfaceItems,
    getComposeStaticActions,
    getReadStaticActions,
    getReadActionDisplayName,
    getComposeActionDisplayName,
    getReadActionIconName,
    getComposeActionIconName,
    getHoverActionDisplayName,
} from 'owa-surface-actions';

const ABSTRACT_COMPOSE_SVG = require('./svg/abstractCompose.svg');
const ABSTRACT_ITEM_PART_SVG = require('./svg/abstractItemPart.svg');
const ABSTRACT_QUICK_ACTION_SLV = require('./svg/abstractQuickActionSingleLine.svg');
const ABSTRACT_QUICK_ACTION_3CV = require('./svg/abstractQuickActionThreeColumn.svg');
const MAX_PREVIEW_ITEM_COUNT = 10;

let styles = require('./MailSurfaceActions.scss');

export interface MailSurfaceActionsProps {
    surfaceType: SurfaceType;
    optionStore?: SurfaceActionsOptionState;
    userOptions?: SurfaceActionsOptions;
}

function isReadingPaneOff() {
    return (
        getUserConfiguration().UserOptions.GlobalReadingPanePositionReact == ReadingPanePosition.Off
    );
}

@reactive({
    optionStore: () => getStore(),
    userOptions: () =>
        getOptionsForFeature<SurfaceActionsOptions>(OwsOptionsFeatureType.SurfaceActions),
})
export default class MailSurfaceActions extends React.Component<MailSurfaceActionsProps, {}> {
    render() {
        let { surfaceType, optionStore, userOptions } = this.props;

        let actions: ActionKey[] = [];
        let addins: AddinCommandSurfaceItem[] = [];
        let selectedActions = [];
        let selectedAddins = [];
        let onActionSelected: (
            actionCollection: string[],
            actionKey: string,
            isSelected: boolean
        ) => void;
        let onAddinSelected: (
            addinCollection: string[],
            addinKey: string,
            isSelected: boolean
        ) => void;
        let actionDisplayNameGetter: (key: ActionKey) => string;
        let actionIconNameGetter: (key: ActionKey) => SurfaceActionIcon;
        let previewImageUri: string;
        let previewClassName: string;
        let description: string;
        let extMode: ExtensibilityModeEnum;

        if (surfaceType == SurfaceType.Compose) {
            actions = getComposeStaticActions().map(a => a.key);
            extMode = ExtensibilityModeEnum.MessageCompose;

            // Get action/addin arrays and slice them to create a copy so we don't modify the real data.
            selectedActions = getFallbackValueIfNull(
                optionStore.composeSurfaceActions,
                userOptions.composeSurfaceActions
            ).slice();
            selectedAddins = getFallbackValueIfNull(
                optionStore.composeSurfaceAddins,
                userOptions.composeSurfaceAddins
            ).slice();
            onActionSelected = (
                actionCollection: string[],
                actionKey: string,
                isSelected: boolean
            ) => {
                selectSurfaceActionsOption(
                    'composeSurfaceActions',
                    actionCollection,
                    actionKey,
                    isSelected
                );
            };
            onAddinSelected = (
                addinCollection: string[],
                addinKey: string,
                isSelected: boolean
            ) => {
                selectSurfaceActionsOption(
                    'composeSurfaceAddins',
                    addinCollection,
                    addinKey,
                    isSelected
                );
            };
            actionDisplayNameGetter = getComposeActionDisplayName;
            actionIconNameGetter = getComposeActionIconName;
            previewImageUri = ABSTRACT_COMPOSE_SVG;
            previewClassName = styles.composePreview;
            description = strings.options_composeSurfaceActions_description;
        } else if (surfaceType == SurfaceType.Read) {
            actions = getReadStaticActions().map(a => a.key);
            extMode = ExtensibilityModeEnum.MessageRead;

            // Get action/addin arrays and slice them to create a copy so we don't modify the real data.
            selectedActions = getFallbackValueIfNull(
                optionStore.readSurfaceActions,
                userOptions.readSurfaceActions
            ).slice();
            selectedAddins = getFallbackValueIfNull(
                optionStore.readSurfaceAddins,
                userOptions.readSurfaceAddins
            ).slice();
            onActionSelected = (
                actionCollection: string[],
                actionKey: string,
                isSelected: boolean
            ) => {
                selectSurfaceActionsOption(
                    'readSurfaceActions',
                    actionCollection,
                    actionKey,
                    isSelected
                );
            };
            onAddinSelected = (
                addinCollection: string[],
                addinKey: string,
                isSelected: boolean
            ) => {
                selectSurfaceActionsOption(
                    'readSurfaceAddins',
                    addinCollection,
                    addinKey,
                    isSelected
                );
            };
            actionDisplayNameGetter = getReadActionDisplayName;
            actionIconNameGetter = getReadActionIconName;
            previewImageUri = ABSTRACT_ITEM_PART_SVG;
            previewClassName = styles.readingPanePreview;
            description = strings.options_readSurfaceActions_description;
        } else if (surfaceType == SurfaceType.Hover) {
            actions = ['Delete', 'Archive', 'PinUnpin', 'ReadUnread', 'FlagUnflag', 'Move'];

            // Get action/addin arrays and slice them to create a copy so we don't modify the real data.
            selectedActions = getFallbackValueIfNull(
                optionStore.hoverSurfaceActions,
                getHoverSurfaceAction()
            ).slice();
            selectedAddins = [];
            onActionSelected = (
                actionCollection: string[],
                actionKey: string,
                isSelected: boolean
            ) => {
                selectSurfaceActionsOption(
                    'hoverSurfaceActions',
                    actionCollection,
                    actionKey,
                    isSelected
                );
            };

            const isSingleLine = isReadingPaneOff();
            onAddinSelected = null;
            actionDisplayNameGetter = getHoverActionDisplayName;
            actionIconNameGetter = getHoverActionIconName;
            previewImageUri = isSingleLine ? ABSTRACT_QUICK_ACTION_SLV : ABSTRACT_QUICK_ACTION_3CV;
            previewClassName = isSingleLine ? styles.hoverPreviewSLV : styles.hoverPreview3CV;
            description = strings.options_mailSurfaceActions_description;
        }
        if (isFeatureEnabled('rp-addins')) {
            addins = getAddinOptionSurfaceItems(extMode) || [];
        }

        return (
            <div>
                <SurfaceActionsShared
                    actions={actions}
                    addins={addins}
                    selectedActions={selectedActions}
                    selectedAddins={selectedAddins}
                    onActionSelected={onActionSelected}
                    onAddinSelected={onAddinSelected}
                    getDisplayNameFromAction={actionDisplayNameGetter}
                    getIconNameFromAction={actionIconNameGetter}
                    isLeftToRight={surfaceType == SurfaceType.Compose}
                    previewImageUri={previewImageUri}
                    previewClassName={previewClassName}
                    maxPreviewItemCount={MAX_PREVIEW_ITEM_COUNT}
                    description={description}
                    surfaceType={surfaceType}
                />
            </div>
        );
    }
}
